import { useEffect, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

export interface TabConfig {
  id: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  content: React.ReactNode;
}

interface TabbedPageProps {
  tabs: TabConfig[];
  defaultTab?: string;
  className?: string;
  tabsClassName?: string;
  contentClassName?: string;
}

export function TabbedPage({
  tabs,
  defaultTab,
  className,
  tabsClassName,
  contentClassName,
}: TabbedPageProps) {
  const searchString = useSearch();
  const [, setLocation] = useLocation();

  // Parse tab from URL query params
  const searchParams = new URLSearchParams(searchString);
  const urlTab = searchParams.get("tab");

  // Determine active tab
  const [activeTab, setActiveTab] = useState<string>(() => {
    if (urlTab && tabs.some(t => t.id === urlTab)) {
      return urlTab;
    }
    return defaultTab || tabs[0]?.id || "";
  });

  // Sync URL when tab changes
  useEffect(() => {
    if (urlTab && tabs.some(t => t.id === urlTab)) {
      setActiveTab(urlTab);
    }
  }, [urlTab, tabs]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    // Update URL with new tab
    const newParams = new URLSearchParams(searchString);
    newParams.set("tab", value);
    setLocation(`?${newParams.toString()}`, { replace: true });
  };

  if (tabs.length === 0) return null;

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className={className}>
      <TabsList className={cn("w-full justify-start h-12 p-1 bg-muted/50", tabsClassName)}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            className={cn(
              "flex items-center gap-2 px-4 py-2 data-[state=active]:bg-background",
              "data-[state=active]:shadow-sm transition-all"
            )}
          >
            {tab.icon && <tab.icon className="h-4 w-4" />}
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.id}
          value={tab.id}
          className={cn("mt-6", contentClassName)}
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

// Page header component for tabbed pages
interface TabbedPageHeaderProps {
  title: string;
  description?: string;
  actions?: React.ReactNode;
  className?: string;
}

export function TabbedPageHeader({ title, description, actions, className }: TabbedPageHeaderProps) {
  return (
    <div className={cn("flex items-start justify-between mb-6", className)}>
      <div>
        <h1 className="text-2xl font-display font-bold tracking-tight">{title}</h1>
        {description && (
          <p className="text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
